import React from "react";
import { GeneralTranslation } from "@swo/storefront";
import { ContentFulComponents, SwarovskiOptikLocaleFrontend } from "@swo/storefront";
import { pipe } from "fp-ts/function";
import * as NEA from "fp-ts/NonEmptyArray";
import * as O from "fp-ts/Option";

import { ComponentSpacing } from "./ComponentSpacing";

import { TextAndImageComponent } from "../../components/TextAndImageComponent/TextAndImageComponent";
import { HeroImageComponent } from "../../components/HeroImageComponent/HeroImageComponent";
import { RelatedArticleSetComponent } from "../../components/RelatedArticleSetComponent/RelatedArticleSetComponent";
import { TextComponent } from "../../components/TextComponent/TextComponent";
import { ImageComponent } from "../../components/ImageComponent/ImageComponent";
import { QuotationFamilyPageComponent } from "../../components/QuotationComponent/QuotationFamilyPageComponent";
import { ImageCarouselComponent } from "../../components/ImageCarouselComponent/ImageCarouselComponent";
import { CTABannerComponent } from "../../components/CTABannerComponent/CTABannerComponent";
import { ProductClickAndInfoComponent } from "../../components/ProductClickAndInfoComponent/ProductClickAndInfoComponent";
import { QuotationStoryComponent } from "../../components/QuotationStoryComponent/QuotationStoryComponent";
import { AccordionComponent } from "../../components/AccordionComponent/AccordionComponent";
import { ImageGalleryComponent } from "../../components/ImageGallery/ImageGallery";
import { OverlappingImagesComponent } from "../../components/OverlappingImagesComponent/OverlappingImagesComponent";
import { RelatedArticleTeasersComponent } from "../../components/RelatedArticleTeasersComponent/RelatedArticleTeasersComponent";
import { HeadlineAndTextComponent } from "../../components/HeadlineAndTextComponent/HeadlineAndTextComponent";
import { ImageAndProductComponent } from "../../components/ImageAndProductComponent/ImageAndProductComponent";
import { ImageDetailsCtaComponent } from "../../components/ImageDetailsCtaComponent/ImageDetailsCtaComponent";
import { ImageAndFeaturesComponent } from "../../components/ImageAndFeaturesComponent/ImageAndFeaturesComponent";
import { ProductPlacementComponent } from "../../components/ProductPlacementComponent/ProductPlacementComponent";
import { IconsAndTextsComponent } from "../../components/IconsAndTextsComponent/IconsAndTextsComponent";
import { TechSpecTeaserComponent } from "../../components/TechSpecTeaserComponent/TechSpecTeaserComponent";
import { AdvertisementsCarouselComponent } from "../../components/AdvertisementsCarouselComponent/AdvertisementsCarouselComponent";
import { ThreeDModelComponent } from "../../components/3DViewerComponent/three-d-model-component";
import { GuidedSellingComponent } from "../../components/GuidedSellingComponent/GuidedSellingComponent";
import { ProductCategoriesComponent } from "../../components/ProductCategoriesComponent/ProductCategoriesComponent";
import { IframeComponent } from "../../components/IframeComponent/IframeComponent";
import { OtherProductsInFamily } from "../product-detail-page-layout/other-products-in-family";
import { enrichedSwarovskiProductFrontendOrd } from "../../domain/swarovski-optik/products/ord";
import { VideoPlayerWithUi } from "../../components/VideoComponent/VideoPlayerWithUi";
import { Translated } from "../../domain/translations/translations";
import { css } from "@emotion/react";
import { CSS_COLORS, CSS_SPACER_SIZES } from "../../styles/styles-css-variables";
import { ContentContainer } from "../../components/_shared-components/arrangement/Container";
import { useComponentLinksStateCtx, useToolbarStateCtx } from "../../components/cms/edit-overlay/ToolbarStateProvider";
import { useContentfulLink } from "../../components/useContentfulLink";
import { fontSize } from "../../styles/fonts";
import { ReactComponent as ContentfulIconSvg } from "../../components/cms/SiteMapComponent/contentful-icon.svg";
import { transformPascalCase } from "../../components/cms/edit-overlay/transform-pascal-case";
import { ClientErrorBoundary } from "./error-boundary";

export const RenderComponentsList: React.FC<{
  components: Array<ContentFulComponents | string | undefined>;
  localeForNumberFormat: SwarovskiOptikLocaleFrontend;
  translations: Translated<GeneralTranslation>;
  previousListLastComponent:
    | ContentFulComponents
    | { __typename: "custom"; node: React.JSX.Element | null }
    | string
    | undefined;
  noSpaceForFirstComponent?: boolean;
}> = ({ components, localeForNumberFormat, translations, previousListLastComponent, noSpaceForFirstComponent }) => {
  const componentLinks = useComponentLinksStateCtx();
  const { state: toolbeltState } = useToolbarStateCtx();

  return (
    <>
      {components.map((component, idx, ctx) => {
        const previousComponent = (idx > 0 ? ctx[idx - 1] : previousListLastComponent) ?? null;

        if (typeof component === "string") {
          if (!toolbeltState) {
            return null;
          }

          let entryId: string | undefined = undefined;

          try {
            const err = JSON.parse(component);

            if (Array.isArray(err)) {
              const e = err.find(e => e?.entryId);

              if (e) {
                entryId = e.entryId;
              }
            } else if (err.sys?.id) {
              entryId = err.sys?.id;
            } else {
              //
            }
          } catch (e) {
            console.error(e);
          }

          return (
            <React.Fragment key={`${component}-${idx}`}>
              <ContentContainer>
                <div
                  css={css`
                    background-color: ${CSS_COLORS.RedTransparentCustom};
                    border: 2px solid ${CSS_COLORS.RedCustom};

                    padding-top: 30px;
                    padding-bottom: 30px;
                    padding-left: 10px;
                    padding-right: 10px;

                    border-radius: 8px;
                    margin-top: ${CSS_SPACER_SIZES.md};
                    margin-bottom: ${CSS_SPACER_SIZES.md};
                  `}
                >
                  <pre
                    css={css`
                      white-space: break-spaces;
                    `}
                  >
                    {JSON.stringify(JSON.parse(component), null, 2)}
                  </pre>
                </div>
              </ContentContainer>

              {componentLinks && entryId ? <ContentAreaComponentLink component={{ entryId }} /> : null}
            </React.Fragment>
          );
        }

        if (!component) {
          return null;
        }

        const standardComponent = getStandardComponent(component);

        return (
          <React.Fragment key={`${component.__typename}-${idx}`}>
            <ClientErrorBoundary>{standardComponent}</ClientErrorBoundary>
            {componentLinks ? <ContentAreaComponentLink component={component} /> : null}
          </React.Fragment>
        );

        function getStandardComponent(component: ContentFulComponents) {
          switch (component.__typename) {
            // Spacing is of course only temporary!
            case "custom": {
              switch (component.name) {
                case "OtherProductsInFamily": {
                  return (
                    <React.Fragment key={`${component.__typename}-${idx}`}>
                      {pipe(
                        component.data.familyEnrichedProducts,
                        NEA.fromArray,
                        O.fold(
                          () => null,
                          nea => {
                            const products = pipe(nea, NEA.sort(enrichedSwarovskiProductFrontendOrd));

                            return (
                              <OtherProductsInFamily
                                key={products.length}
                                headline={undefined}
                                products={products}
                                selectedProductKey={undefined}
                                centeredIfNotFilled={true}
                              />
                            );
                          },
                        ),
                      )}
                    </React.Fragment>
                  );
                }

                default: {
                  const _: never = component.name;
                  return;
                }
              }
            }

            case "ComponentTextAndImage":
              return (
                <React.Fragment key={`${component.__typename}-${idx}`}>
                  <ComponentSpacing
                    previousComponent={previousComponent}
                    index={idx}
                    componentTypeName={component.__typename}
                    noSpaceForFirstComponent={!!noSpaceForFirstComponent}
                  />
                  <TextAndImageComponent {...component} />
                </React.Fragment>
              );

            case "ComponentHeroImage":
              return (
                <React.Fragment key={`${component.__typename}-${idx}`}>
                  <ComponentSpacing
                    previousComponent={previousComponent}
                    index={idx}
                    componentTypeName={component.__typename}
                    noSpaceForFirstComponent={!!noSpaceForFirstComponent}
                  />
                  <HeroImageComponent
                    {...component}
                    isFirstHeroComponentOnPage={
                      ctx.findIndex(v => (typeof v === "string" ? false : v?.__typename === "ComponentHeroImage")) ===
                      idx
                    }
                  />
                </React.Fragment>
              );

            case "ComponentRelatedArticle": {
              return (
                <React.Fragment key={`${component.__typename}-${idx}`}>
                  <ComponentSpacing
                    previousComponent={previousComponent}
                    index={idx}
                    componentTypeName={component.__typename}
                    noSpaceForFirstComponent={!!noSpaceForFirstComponent}
                  />
                  <RelatedArticleSetComponent {...component} />
                </React.Fragment>
              );
            }

            case "ComponentVideo":
              return (
                <React.Fragment key={`${component.__typename}-${idx}`}>
                  <ComponentSpacing
                    previousComponent={previousComponent}
                    index={idx}
                    componentTypeName={component.__typename}
                    noSpaceForFirstComponent={!!noSpaceForFirstComponent}
                  />
                  <VideoPlayerWithUi {...component} resolutionPickerStrategy={"middle"} mode={"fill"} />
                </React.Fragment>
              );

            case "ComponentText":
              return (
                <React.Fragment key={`${component.__typename}-${idx}`}>
                  <ComponentSpacing
                    previousComponent={previousComponent}
                    index={idx}
                    componentTypeName={component.__typename}
                    noSpaceForFirstComponent={!!noSpaceForFirstComponent}
                  />
                  <TextComponent {...component} />
                </React.Fragment>
              );

            case "ComponentImage":
              return (
                <React.Fragment key={`${component.__typename}-${idx}`}>
                  <ComponentSpacing
                    previousComponent={previousComponent}
                    index={idx}
                    componentTypeName={component.__typename}
                    noSpaceForFirstComponent={!!noSpaceForFirstComponent}
                  />
                  <ImageComponent {...component} />
                </React.Fragment>
              );

            case "ComponentQuotationFamilyPage":
              return (
                <React.Fragment key={`${component.__typename}-${idx}`}>
                  <ComponentSpacing
                    previousComponent={previousComponent}
                    index={idx}
                    componentTypeName={component.__typename}
                    noSpaceForFirstComponent={!!noSpaceForFirstComponent}
                  />
                  <QuotationFamilyPageComponent {...component} />
                </React.Fragment>
              );

            case "ComponentQuotationStoryPage":
              return (
                <React.Fragment key={`${component.__typename}-${idx}`}>
                  <ComponentSpacing
                    previousComponent={previousComponent}
                    index={idx}
                    componentTypeName={component.__typename}
                    noSpaceForFirstComponent={!!noSpaceForFirstComponent}
                  />
                  <QuotationStoryComponent {...component} />
                </React.Fragment>
              );

            case "ComponentImageCarousel":
              return (
                <React.Fragment key={`${component.__typename}-${idx}`}>
                  <ComponentSpacing
                    previousComponent={previousComponent}
                    index={idx}
                    componentTypeName={component.__typename}
                    noSpaceForFirstComponent={!!noSpaceForFirstComponent}
                  />
                  <ImageCarouselComponent {...component} width={"content"} scrollSnap={false} />
                </React.Fragment>
              );

            case "ComponentAccordion":
              return (
                <React.Fragment key={`${component.__typename}-${idx}`}>
                  <ComponentSpacing
                    previousComponent={previousComponent}
                    index={idx}
                    componentTypeName={component.__typename}
                    noSpaceForFirstComponent={!!noSpaceForFirstComponent}
                  />
                  <AccordionComponent {...component} />
                </React.Fragment>
              );

            case "ComponentProductClickAndInfo":
              return (
                <React.Fragment key={`${component.__typename}-${idx}`}>
                  <ComponentSpacing
                    previousComponent={previousComponent}
                    index={idx}
                    componentTypeName={component.__typename}
                    noSpaceForFirstComponent={!!noSpaceForFirstComponent}
                  />
                  <ProductClickAndInfoComponent {...component} />
                </React.Fragment>
              );

            case "ComponentImageGallery":
              return (
                <React.Fragment key={`${component.__typename}-${idx}`}>
                  <ComponentSpacing
                    previousComponent={previousComponent}
                    index={idx}
                    componentTypeName={component.__typename}
                    noSpaceForFirstComponent={!!noSpaceForFirstComponent}
                  />
                  <ImageGalleryComponent {...component} loadMoreLabel={translations("loadMoreLabel")} />
                </React.Fragment>
              );

            case "ComponentCtaBanner":
              return (
                <React.Fragment key={`${component.__typename}-${idx}`}>
                  <ComponentSpacing
                    previousComponent={previousComponent}
                    index={idx}
                    componentTypeName={component.__typename}
                    noSpaceForFirstComponent={!!noSpaceForFirstComponent}
                  />
                  <CTABannerComponent {...component} />
                </React.Fragment>
              );

            case "ComponentOverlappingImages":
              return (
                <React.Fragment key={`${component.__typename}-${idx}`}>
                  <ComponentSpacing
                    previousComponent={previousComponent}
                    index={idx}
                    componentTypeName={component.__typename}
                    noSpaceForFirstComponent={!!noSpaceForFirstComponent}
                  />
                  <OverlappingImagesComponent {...component} />
                </React.Fragment>
              );

            case "ComponentRelatedArticleTeasers":
              return (
                <React.Fragment key={`${component.__typename}-${idx}`}>
                  <ComponentSpacing
                    previousComponent={previousComponent}
                    index={idx}
                    componentTypeName={component.__typename}
                    noSpaceForFirstComponent={!!noSpaceForFirstComponent}
                  />
                  <RelatedArticleTeasersComponent {...component} />
                </React.Fragment>
              );
            case "ComponentHeadlineAndText":
              return (
                <React.Fragment key={`${component.__typename}-${idx}`}>
                  <ComponentSpacing
                    previousComponent={previousComponent}
                    index={idx}
                    componentTypeName={component.__typename}
                    noSpaceForFirstComponent={!!noSpaceForFirstComponent}
                  />
                  <HeadlineAndTextComponent {...component} />
                </React.Fragment>
              );

            case "ComponentImageAndProduct":
              return (
                <React.Fragment key={`${component.__typename}-${idx}`}>
                  <ComponentSpacing
                    previousComponent={previousComponent}
                    index={idx}
                    componentTypeName={component.__typename}
                    noSpaceForFirstComponent={!!noSpaceForFirstComponent}
                  />
                  <ImageAndProductComponent
                    {...component}
                    localeForNumberFormat={localeForNumberFormat}
                    photographyAuthorLabelTranslation={translations("photographyLabel")}
                  />
                </React.Fragment>
              );

            case "ComponentImageDetails":
              return (
                <React.Fragment key={`${component.__typename}-${idx}`}>
                  <ComponentSpacing
                    previousComponent={previousComponent}
                    index={idx}
                    componentTypeName={component.__typename}
                    noSpaceForFirstComponent={!!noSpaceForFirstComponent}
                  />
                  <ImageDetailsCtaComponent {...component} />
                </React.Fragment>
              );

            case "ComponentImageAndFeatures":
              return (
                <React.Fragment key={`${component.__typename}-${idx}`}>
                  <ComponentSpacing
                    previousComponent={previousComponent}
                    index={idx}
                    componentTypeName={component.__typename}
                    noSpaceForFirstComponent={!!noSpaceForFirstComponent}
                  />
                  <ImageAndFeaturesComponent {...component} />
                </React.Fragment>
              );

            case "ComponentProductPlacement":
              return (
                <React.Fragment key={`${component.__typename}-${idx}`}>
                  <ComponentSpacing
                    previousComponent={previousComponent}
                    index={idx}
                    componentTypeName={component.__typename}
                    noSpaceForFirstComponent={!!noSpaceForFirstComponent}
                  />
                  <ProductPlacementComponent {...component} />
                </React.Fragment>
              );

            case "ComponentIconsAndTexts":
              return (
                <React.Fragment key={`${component.__typename}-${idx}`}>
                  <ComponentSpacing
                    previousComponent={previousComponent}
                    index={idx}
                    componentTypeName={component.__typename}
                    noSpaceForFirstComponent={!!noSpaceForFirstComponent}
                  />
                  <IconsAndTextsComponent {...component} />
                </React.Fragment>
              );

            case "ComponentTechSpecTeaser":
              return (
                <React.Fragment key={`${component.__typename}-${idx}`}>
                  <ComponentSpacing
                    previousComponent={previousComponent}
                    index={idx}
                    componentTypeName={component.__typename}
                    noSpaceForFirstComponent={!!noSpaceForFirstComponent}
                  />
                  <TechSpecTeaserComponent {...component} localeForNumberFormat={localeForNumberFormat} />
                </React.Fragment>
              );
            case "ComponentAdvertisementsCarousel":
              return (
                <React.Fragment key={`${component.__typename}-${idx}`}>
                  <ComponentSpacing
                    previousComponent={previousComponent}
                    index={idx}
                    componentTypeName={component.__typename}
                    noSpaceForFirstComponent={!!noSpaceForFirstComponent}
                  />
                  <AdvertisementsCarouselComponent {...component} />
                </React.Fragment>
              );

            case "Component3DModel": {
              return (
                <React.Fragment key={`${component.__typename}-${idx}`}>
                  <ComponentSpacing
                    previousComponent={previousComponent}
                    index={idx}
                    componentTypeName={component.__typename}
                    noSpaceForFirstComponent={!!noSpaceForFirstComponent}
                  />
                  <ThreeDModelComponent {...component} />
                </React.Fragment>
              );
            }
            case "ComponentGuidedSelling": {
              return (
                <React.Fragment key={`${component.__typename}-${idx}`}>
                  <ComponentSpacing
                    previousComponent={previousComponent}
                    index={idx}
                    componentTypeName={component.__typename}
                    noSpaceForFirstComponent={!!noSpaceForFirstComponent}
                  />
                  <GuidedSellingComponent {...component} />
                </React.Fragment>
              );
            }

            case "ComponentProductCategories": {
              return (
                <React.Fragment key={`${component.__typename}-${idx}`}>
                  <ComponentSpacing
                    previousComponent={previousComponent}
                    index={idx}
                    componentTypeName={component.__typename}
                    noSpaceForFirstComponent={!!noSpaceForFirstComponent}
                  />
                  <ProductCategoriesComponent {...component} />
                </React.Fragment>
              );
            }

            case "ComponentIframe": {
              return (
                <React.Fragment key={`${component.__typename}-${idx}`}>
                  <ComponentSpacing
                    previousComponent={previousComponent}
                    index={idx}
                    componentTypeName={component.__typename}
                    noSpaceForFirstComponent={!!noSpaceForFirstComponent}
                  />
                  <IframeComponent {...component} />
                </React.Fragment>
              );
            }

            default: {
              console.warn(`Found component`, component, "with no matching renderer.");

              return null;
            }
          }
        }
      })}
    </>
  );
};

const ContentAreaComponentLink: React.FC<{ component: ContentFulComponents | { entryId: string } }> = ({
  component,
}) => {
  const contentfulLink = useContentfulLink();
  const name = "__typename" in component ? `${transformPascalCase(component.__typename)}` : "Unknown";

  const sysId = "entryId" in component ? component.entryId : component.sys.id;

  if (!sysId) {
    return null;
  }

  const entryLink = contentfulLink({
    entryId: sysId,
  });

  return (
    <div
      className="u-relative js-contentful-entry-link"
      css={css`
        width: 100%;
        height: 5px;
        background: ${CSS_COLORS.GreenCustom};
      `}
    >
      <span
        onClick={() => window.open(entryLink, "_blank")}
        className="u-absolute u-left-0"
        css={css`
          display: flex;
          align-items: center;
          justify-content: center;

          cursor: pointer;

          top: -1.5em;
          z-index: 100;

          background: ${CSS_COLORS.GreenTransparent};
          padding: 2px 4px;
        `}
      >
        <div
          css={css`
            transition: transform 0.2s linear;
            &:hover {
              transform: scale(1.1);
            }

            padding: 4px;
            border-radius: 100%;

            width: 24px;
            height: 24px;
          `}
        >
          <ContentfulIconSvg title={`Entry ${sysId} in Contentful`} />
        </div>

        <span className="" css={[fontSize.x2s]}>
          {name}
        </span>
      </span>
    </div>
  );
};
